export interface QRTemplate {
  id: string;
  name: string;
  description: string;
  type: 'url' | 'text' | 'email' | 'phone' | 'sms' | 'wifi' | 'vcard' | 'event';
  icon: string;
  category: 'business' | 'personal' | 'social' | 'marketing' | 'events';
  customization?: {
    foregroundColor?: string;
    backgroundColor?: string;
    errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
  };
  fields: {
    [key: string]: string;
  };
}

export const qrTemplates: QRTemplate[] = [
  // Business Templates
  {
    id: 'business-card',
    name: 'Business Card',
    description: 'Professional vCard with full contact details',
    type: 'vcard',
    icon: '👔',
    category: 'business',
    customization: {
      foregroundColor: '#1e40af',
      errorCorrectionLevel: 'H',
    },
    fields: {
      firstName: 'John',
      lastName: 'Doe',
      organization: 'Your Company',
      title: 'CEO',
      email: 'john@company.com',
      phone: '+1234567890',
      website: 'https://company.com',
    },
  },
  {
    id: 'company-website',
    name: 'Company Website',
    description: 'Link to your company website',
    type: 'url',
    icon: '🌐',
    category: 'business',
    customization: {
      foregroundColor: '#059669',
      errorCorrectionLevel: 'M',
    },
    fields: {
      content: 'https://yourcompany.com',
    },
  },
  {
    id: 'linkedin-profile',
    name: 'LinkedIn Profile',
    description: 'Direct link to your LinkedIn',
    type: 'url',
    icon: '💼',
    category: 'business',
    customization: {
      foregroundColor: '#0077b5',
      errorCorrectionLevel: 'M',
    },
    fields: {
      content: 'https://linkedin.com/in/yourprofile',
    },
  },

  // Personal Templates
  {
    id: 'personal-contact',
    name: 'Personal Contact',
    description: 'Share your personal contact info',
    type: 'vcard',
    icon: '📱',
    category: 'personal',
    fields: {
      firstName: 'Jane',
      lastName: 'Smith',
      email: 'jane@email.com',
      phone: '+1234567890',
    },
  },
  {
    id: 'home-wifi',
    name: 'Home WiFi',
    description: 'Easy WiFi sharing for guests',
    type: 'wifi',
    icon: '📶',
    category: 'personal',
    customization: {
      errorCorrectionLevel: 'Q',
    },
    fields: {
      ssid: 'My Home WiFi',
      password: 'password123',
      encryption: 'WPA',
    },
  },

  // Social Media Templates
  {
    id: 'instagram',
    name: 'Instagram Profile',
    description: 'Link to your Instagram',
    type: 'url',
    icon: '📸',
    category: 'social',
    customization: {
      foregroundColor: '#e4405f',
      errorCorrectionLevel: 'M',
    },
    fields: {
      content: 'https://instagram.com/yourusername',
    },
  },
  {
    id: 'facebook-page',
    name: 'Facebook Page',
    description: 'Link to your Facebook page',
    type: 'url',
    icon: '👥',
    category: 'social',
    customization: {
      foregroundColor: '#1877f2',
      errorCorrectionLevel: 'M',
    },
    fields: {
      content: 'https://facebook.com/yourpage',
    },
  },
  {
    id: 'twitter',
    name: 'Twitter/X Profile',
    description: 'Link to your Twitter/X',
    type: 'url',
    icon: '🐦',
    category: 'social',
    customization: {
      foregroundColor: '#1da1f2',
      errorCorrectionLevel: 'M',
    },
    fields: {
      content: 'https://twitter.com/yourusername',
    },
  },
  {
    id: 'youtube',
    name: 'YouTube Channel',
    description: 'Link to your YouTube channel',
    type: 'url',
    icon: '▶️',
    category: 'social',
    customization: {
      foregroundColor: '#ff0000',
      errorCorrectionLevel: 'M',
    },
    fields: {
      content: 'https://youtube.com/@yourchannel',
    },
  },
  {
    id: 'tiktok',
    name: 'TikTok Profile',
    description: 'Link to your TikTok',
    type: 'url',
    icon: '🎵',
    category: 'social',
    customization: {
      foregroundColor: '#000000',
      errorCorrectionLevel: 'M',
    },
    fields: {
      content: 'https://tiktok.com/@yourusername',
    },
  },

  // Marketing Templates
  {
    id: 'product-page',
    name: 'Product Page',
    description: 'Link to your product or service',
    type: 'url',
    icon: '🛍️',
    category: 'marketing',
    customization: {
      foregroundColor: '#7c3aed',
      errorCorrectionLevel: 'M',
    },
    fields: {
      content: 'https://yourstore.com/product',
    },
  },
  {
    id: 'special-offer',
    name: 'Special Offer',
    description: 'Promote a discount or deal',
    type: 'url',
    icon: '🎁',
    category: 'marketing',
    customization: {
      foregroundColor: '#dc2626',
      errorCorrectionLevel: 'M',
    },
    fields: {
      content: 'https://yoursite.com/promo',
    },
  },
  {
    id: 'app-download',
    name: 'App Download',
    description: 'Link to app store download',
    type: 'url',
    icon: '📲',
    category: 'marketing',
    customization: {
      foregroundColor: '#0891b2',
      errorCorrectionLevel: 'M',
    },
    fields: {
      content: 'https://apps.apple.com/app/yourapp',
    },
  },
  {
    id: 'email-newsletter',
    name: 'Email Newsletter',
    description: 'Subscribe to newsletter',
    type: 'email',
    icon: '📧',
    category: 'marketing',
    fields: {
      content: 'newsletter@company.com',
      subject: 'Newsletter Subscription',
      body: 'I would like to subscribe to your newsletter',
    },
  },

  // Events Templates
  {
    id: 'event-rsvp',
    name: 'Event RSVP',
    description: 'Event registration link',
    type: 'url',
    icon: '🎉',
    category: 'events',
    customization: {
      foregroundColor: '#f59e0b',
      errorCorrectionLevel: 'M',
    },
    fields: {
      content: 'https://eventbrite.com/your-event',
    },
  },
  {
    id: 'restaurant-menu',
    name: 'Restaurant Menu',
    description: 'Digital menu access',
    type: 'url',
    icon: '🍽️',
    category: 'events',
    fields: {
      content: 'https://yourrestaurant.com/menu',
    },
  },
  {
    id: 'feedback-form',
    name: 'Feedback Form',
    description: 'Collect customer feedback',
    type: 'url',
    icon: '📝',
    category: 'events',
    fields: {
      content: 'https://forms.google.com/your-form',
    },
  },
  {
    id: 'contact-sms',
    name: 'Quick SMS',
    description: 'Pre-filled SMS message',
    type: 'sms',
    icon: '💬',
    category: 'personal',
    fields: {
      phone: '+1234567890',
      message: 'Hi! I scanned your QR code.',
    },
  },
];

export const getTemplatesByCategory = (category: string) => {
  return qrTemplates.filter((template) => template.category === category);
};

export const getTemplateById = (id: string) => {
  return qrTemplates.find((template) => template.id === id);
};

export const categories = [
  { id: 'business', name: 'Business', icon: '💼', color: 'blue' },
  { id: 'personal', name: 'Personal', icon: '👤', color: 'green' },
  { id: 'social', name: 'Social Media', icon: '📱', color: 'purple' },
  { id: 'marketing', name: 'Marketing', icon: '📈', color: 'orange' },
  { id: 'events', name: 'Events', icon: '🎫', color: 'pink' },
];




